import { forIn } from "lodash";

import { ChartFamily, ChartFormatters, ChartName, ChartSettings } from "@/classes/BaseChart/index";
import { EMPIRIC_CRITICAL_DATA_RANGE, calcDataRange } from "@/helpers/charts";
import { DEFAULT_DECIMALS } from "@/helpers/constants";
import { getChartAllSettings } from "@/settings/charts";
import { ChartDataRecord } from "@/types/charts";
import { InputDictionary } from "@/types/common";

class ChartWithFormatters {
  declare chartName: ChartName;
  declare chartFamily: ChartFamily;
  declare settings: ChartSettings;
  declare formatters: ChartFormatters;
  declare inputsDictionary: Record<string, InputDictionary[]>;
  declare metricsDictionary: InputDictionary[];
  declare settingsModule: string;

  private getFormatter() {
    const allSettings = getChartAllSettings(this.chartName, this.chartFamily, this.settingsModule);

    this.formatters = {};
    forIn(allSettings, (setting, key) => {
      const dict = this.inputsDictionary[setting.dictionary];
      if (!Array.isArray(dict)) return;

      const selected = Array.isArray(this.settings[key]) ? this.settings[key] : [this.settings[key]];
      selected.forEach((value: unknown) => {
        const selectedMetric = dict.find((el: InputDictionary) => el.value === value);
        if (selectedMetric?.formatter) {
          this.formatters[selectedMetric.value as string] = selectedMetric.formatter;
        }
      });
    });
  }

  // We use 'any' here, because of AmCharts bug, when AmCharts can
  // take object for formatter, but in TS described only string | undefined
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  protected getMetricFormatter(metricValue: string, config = {}): any {
    const metric = this.metricsDictionary.find((metric) => {
      return metric.value === metricValue;
    });

    return metric?.configFormatter ? { ...metric?.configFormatter, ...config } : {};
  }

  // We use any here because getMetricFormatter returns any, see comments before this function.
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  protected getCompactAxesMetricFormatter(metricValue: string, data: ChartDataRecord[]): any {
    return this.getMetricFormatter(metricValue, {
      notation: "compact",
      maximumFractionDigits: this.getPrecisionByDataset(data, metricValue),
    });
  }

  protected getPrecisionByDataset(data: ChartDataRecord[], key: string): number | undefined {
    return calcDataRange(data, key) <= EMPIRIC_CRITICAL_DATA_RANGE ? DEFAULT_DECIMALS : undefined;
  }

  protected getDataLabelsFormatterConfig(): Record<string, unknown> {
    const config: Record<string, unknown> = {};

    if (this.shouldUseCompactDataLabels()) {
      config.notation = "compact";
    }

    const decimals = this.getDataLabelsDecimals();
    if (decimals !== undefined) {
      config.minimumFractionDigits = decimals;
      config.maximumFractionDigits = decimals;
    }

    return config;
  }

  protected shouldUseCompactDataLabels(): boolean {
    return Boolean(this.settings?.dataLabelsCompact);
  }

  protected getDataLabelsDecimals(): number | undefined {
    const raw = this.settings?.dataLabelsDecimals;

    if (raw === undefined || raw === null || raw === "") {
      return undefined;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return undefined;
    }

    return Math.floor(parsed);
  }

  protected getDataLabelsTopLimit(): number | undefined {
    const rawLimit = this.settings?.dataLabelsTopEntries;

    if (rawLimit === undefined || rawLimit === null || rawLimit === "") {
      return undefined;
    }

    const parsedLimit = Number(rawLimit);

    if (!Number.isFinite(parsedLimit) || parsedLimit <= 0) {
      return undefined;
    }

    return Math.floor(parsedLimit);
  }
}

export default ChartWithFormatters;
